"use client"

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react"
import { Fragment, useMemo, useState } from "react"
import ReactCountryFlag from "react-country-flag"
import { useParams, usePathname, useRouter } from "next/navigation"
import { HttpTypes } from "@medusajs/types"

import { discardCartOnRegionChange } from "@/lib/data/cart"

type CountryOption = {
  country: string
  region: string
  label: string
}

type CountrySelectProps = {
  regions: HttpTypes.StoreRegion[]
}

const CountrySelect = ({ regions }: CountrySelectProps) => {
  const router = useRouter()
  const params = useParams()
  const pathname = usePathname()

  const countryCode = String(
    Array.isArray(params.locale) ? params.locale[0] : params.locale || ""
  ).toLowerCase()

  const currentPath = pathname.split(`/${countryCode}`)[1] || ""

  const options = useMemo(() => {
    return regions
      ?.map((r) => {
        return r.countries?.map((c) => ({
          country: (c.iso_2 || "").toLowerCase(),
          region: r.id,
          label: c.display_name,
        }))
      })
      .flat()
      .filter((o): o is CountryOption => !!o?.country)
      .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""))
  }, [regions])

  // Always controlled: null = no selection (never undefined → Listbox warning)
  const selectedFromUrl = useMemo(
    () => options?.find((o) => o.country === countryCode) ?? null,
    [options, countryCode]
  )
  const [optimistic, setOptimistic] = useState<CountryOption | null>(null)
  const [switching, setSwitching] = useState(false)
  const current = optimistic ?? selectedFromUrl

  const hrefFor = (country: string) => `/${country}${currentPath}`

  const handleChange = (option: CountryOption | null) => {
    if (!option?.country || option.country === countryCode || switching) {
      return
    }

    setSwitching(true)
    setOptimistic(option)

    const href = hrefFor(option.country)
    router.prefetch(href)

    if (selectedFromUrl?.region && option.region !== selectedFromUrl.region) {
      void discardCartOnRegionChange()
    }

    window.location.assign(href)
  }

  return (
    <div>
      <Listbox by="country" value={current} onChange={handleChange}>
        <ListboxButton
          disabled={switching}
          aria-label="Select region"
          className="relative w-12 flex justify-center items-center h-10 bg-component-secondary text-left cursor-pointer focus:outline-none border rounded-lg focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-gray-300 focus-visible:ring-offset-2 focus-visible:border-gray-300 text-base-regular disabled:opacity-60"
        >
          <div className="txt-compact-small flex items-start mx-auto">
            {current && (
              <span className="txt-compact-small flex items-center gap-x-2">
                {/* @ts-ignore */}
                <ReactCountryFlag
                  svg
                  style={{
                    width: "16px",
                    height: "16px",
                  }}
                  countryCode={current.country ?? ""}
                />
              </span>
            )}
          </div>
        </ListboxButton>
        <div className="flex relative w-14">
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <ListboxOptions className="no-scrollbar absolute z-20 overflow-auto text-small-regular bg-white rounded-lg border-top-0 max-h-60 focus:outline-none sm:text-sm ring-1 ring-inset ring-gray-200 ">
              {options?.map((o) => (
                <ListboxOption
                  key={o.country}
                  value={o}
                  onMouseEnter={() => router.prefetch(hrefFor(o.country))}
                  onFocus={() => router.prefetch(hrefFor(o.country))}
                  className="cursor-pointer select-none relative w-12 h-12 hover:bg-gray-50 py-4 border-b"
                >
                  <span className="flex items-center gap-x-2 pl-4">
                    {/* @ts-ignore */}
                    <ReactCountryFlag
                      svg
                      style={{
                        width: "16px",
                        height: "16px",
                      }}
                      countryCode={o.country}
                    />
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      </Listbox>
    </div>
  )
}

export default CountrySelect
